/**
 * UnitCountsCard Component
 *
 * Displays unit counts by brand for New and Used vehicles with vertical bar charts.
 * Each brand gets its own tile for a responsive, spacious layout.
 * Uses the same color scheme as the Gross Progression chart.
 */

import React, { useState } from 'react';
import { DailyUnitCounts, YearComparisonData } from '../../types/forecast';
import { DepartmentKey } from './DepartmentTabs';
import { getLocationBrands, ensureAllBrands } from '../../config/dealershipBrands';
import { calculateTrendingUnits } from '../../utils/trendCalculation';

// Color scheme matching GrossProgressionChart
const COLORS = {
  currentYear: '#2563eb',  // Blue-600
  lastYear: '#9ca3af',     // Gray-400
  average: '#10b981',      // Emerald-500 (Green)
  unprocessed: '#93c5fd',  // Blue-300 (lighter blue for unprocessed)
  projectedPace: '#8b5cf6', // Violet-500 (Purple - matches projection theme)
};

/** Unprocessed units by brand/category */
interface UnprocessedUnits {
  unitsByBrand: Record<string, number>;
  unitsByCategory: Record<string, number>;
  totalNewUnits: number;
  totalUsedUnits: number;
}

interface UnitCountsCardProps {
  currentYear: number;
  currentUnits: DailyUnitCounts | null;
  lastYearData: YearComparisonData | null;
  /** All comparison data by year for calculating averages */
  comparisonByYear?: Record<number, YearComparisonData>;
  daysRemaining: number;
  /** Number of working days elapsed (completed) */
  daysElapsed?: number;
  /** Total working days in the month */
  totalWorkingDays?: number;
  selectedDepartment?: DepartmentKey;
  locationId: number;
  /** Unprocessed units (sold but not yet accounting-processed) */
  unprocessedUnits?: UnprocessedUnits;
  /** Swap units for New Vehicles (dealer trades — not counted toward sold unit goals) */
  swapNewUnits?: number;
  /** Wholesale units for Used Vehicles (not counted toward sold unit goals) */
  wholesaleUsedUnits?: number;
  /** Target units from projections (for the selected department) */
  targetUnits?: number;
  /** Callback when "Units Sold" is clicked (for deal details modal) */
  onUnitsClick?: (department: 'NEW_VEHICLE' | 'USED_VEHICLE') => void;
}

/**
 * Individual brand tile with vertical bar chart
 */
interface BrandTileProps {
  brand: string;
  currentUnits: number;
  lastYearUnits: number | null;
  averageUnits: number | null;
  currentYear: number;
  maxValue: number;
  /** Unprocessed units for this brand (stacked on top of current) */
  unprocessedUnits?: number;
}

const BAR_MAX_HEIGHT = 80; // pixels
const MIN_UNPROCESSED_HEIGHT = 6; // Minimum height in pixels for unprocessed segment to be visible

const BrandTile: React.FC<BrandTileProps> = ({
  brand,
  currentUnits,
  lastYearUnits,
  averageUnits,
  currentYear,
  maxValue,
  unprocessedUnits = 0,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const totalUnits = currentUnits + unprocessedUnits;
  const hasUnprocessed = unprocessedUnits > 0;

  // Calculate bar heights in pixels (use totalUnits for current year bar)
  const processedHeight = maxValue > 0 ? (currentUnits / maxValue) * BAR_MAX_HEIGHT : 0;
  // Ensure unprocessed section is visible when it exists (min 6px)
  const rawUnprocessedHeight = maxValue > 0 ? (unprocessedUnits / maxValue) * BAR_MAX_HEIGHT : 0;
  const unprocessedHeight = hasUnprocessed ? Math.max(rawUnprocessedHeight, MIN_UNPROCESSED_HEIGHT) : 0;
  const lastYearHeight = lastYearUnits !== null && maxValue > 0 ? (lastYearUnits / maxValue) * BAR_MAX_HEIGHT : 0;
  const averageHeight = averageUnits !== null && maxValue > 0 ? (averageUnits / maxValue) * BAR_MAX_HEIGHT : 0;

  // Calculate variance from last year (using total including unprocessed)
  const variance = lastYearUnits !== null ? totalUnits - lastYearUnits : null;
  const isPositive = variance !== null && variance >= 0;

  return (
    <div
      className="relative bg-gray-50 rounded-lg p-3 flex flex-col items-center cursor-pointer hover:bg-gray-100 transition-colors"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Tooltip - styled to match GrossProgressionChart tooltip */}
      {showTooltip && (
        <div className="absolute bottom-full mb-2 z-10 bg-white border border-gray-200 rounded-lg shadow-lg p-3 whitespace-nowrap">
          <p className="font-semibold text-gray-900 mb-2">{brand}</p>
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.currentYear }} />
              <span className="text-gray-600">{currentYear} Processed:</span>
              <span className="font-medium" style={{ color: COLORS.currentYear }}>{currentUnits}</span>
            </div>
            {hasUnprocessed && (
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.unprocessed }} />
                <span className="text-gray-600">{currentYear} Unprocessed:</span>
                <span className="font-medium" style={{ color: COLORS.unprocessed }}>{unprocessedUnits}</span>
              </div>
            )}
            {hasUnprocessed && (
              <div className="flex items-center gap-2 text-sm font-medium border-t border-gray-100 pt-1 mt-1">
                <span className="text-gray-600">{currentYear} Total:</span>
                <span style={{ color: COLORS.currentYear }}>{totalUnits}</span>
              </div>
            )}
            {lastYearUnits !== null && (
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.lastYear }} />
                <span className="text-gray-600">{currentYear - 1}:</span>
                <span className="font-medium" style={{ color: COLORS.lastYear }}>{lastYearUnits}</span>
              </div>
            )}
            {averageUnits !== null && (
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.average }} />
                <span className="text-gray-600">3Y Avg:</span>
                <span className="font-medium" style={{ color: COLORS.average }}>{Math.round(averageUnits)}</span>
              </div>
            )}
            {variance !== null && (
              <div className={`flex items-center gap-2 text-sm pt-1 border-t border-gray-200 mt-1`}>
                <div className={`w-3 h-3 rounded-full ${isPositive ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-gray-600">vs LY:</span>
                <span className={`font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                  {isPositive ? '+' : ''}{variance}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Brand label at top */}
      <div className="text-sm font-semibold text-gray-800 mb-2 text-center">
        {brand}
      </div>

      {/* Bars container */}
      <div className="flex items-end justify-center gap-2 mb-2" style={{ height: BAR_MAX_HEIGHT }}>
        {/* Current year bar - stacked: processed (bottom) + unprocessed (top) */}
        <div className="w-6 flex flex-col-reverse">
          {/* Processed section (bottom) */}
          <div
            className="w-full transition-all duration-300"
            style={{
              height: Math.max(processedHeight, 4),
              backgroundColor: COLORS.currentYear,
              borderTopLeftRadius: hasUnprocessed ? 0 : 4,
              borderTopRightRadius: hasUnprocessed ? 0 : 4,
            }}
          />
          {/* Unprocessed section (top) - lighter color */}
          {hasUnprocessed && (
            <div
              className="w-full rounded-t transition-all duration-300"
              style={{
                height: unprocessedHeight,
                backgroundColor: COLORS.unprocessed,
              }}
            />
          )}
        </div>

        {/* Last year bar */}
        {lastYearUnits !== null && (
          <div
            className="w-6 rounded-t transition-all duration-300"
            style={{
              height: Math.max(lastYearHeight, 4),
              backgroundColor: COLORS.lastYear,
            }}
          />
        )}

        {/* Average bar */}
        {averageUnits !== null && (
          <div
            className="w-6 rounded-t transition-all duration-300"
            style={{
              height: Math.max(averageHeight, 4),
              backgroundColor: COLORS.average,
            }}
          />
        )}
      </div>

      {/* Current count - shows total (processed + unprocessed) */}
      <div className="text-2xl font-bold text-gray-900">{totalUnits}</div>

      {/* Variance indicator */}
      {variance !== null && (
        <div className={`text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
          {isPositive ? '+' : ''}{variance}
        </div>
      )}
    </div>
  );
};

/**
 * ConsolidatedUnitSection - Simplified unit display for consolidated view
 * Shows totals only without per-brand breakdown, but still includes Unit Trend bar
 */
interface ConsolidatedUnitSectionProps {
  title: string;
  totalCurrent: number;
  totalLastYear: number | null;
  currentYear: number;
  totalUnprocessed?: number;
  trendingUnits?: number | null;
  trendLabel?: string;
}

const ConsolidatedUnitSection: React.FC<ConsolidatedUnitSectionProps> = ({
  title,
  totalCurrent,
  totalLastYear,
  currentYear,
  totalUnprocessed = 0,
  trendingUnits,
  trendLabel = 'Unit Trend',
}) => {
  const combinedTotal = totalCurrent + totalUnprocessed;
  const hasUnprocessed = totalUnprocessed > 0;
  const hasTrending = trendingUnits !== null && trendingUnits !== undefined && trendingUnits > 0;

  // Variance uses combined total (processed + unprocessed)
  const variance = totalLastYear !== null ? combinedTotal - totalLastYear : null;
  const isPositive = variance !== null && variance >= 0;

  // Calculate trend progress (current sold vs trending end-of-month)
  const trendPercent = hasTrending && trendingUnits! > 0
    ? Math.min(Math.round((combinedTotal / trendingUnits!) * 100), 100)
    : 0;

  return (
    <div className="mb-3 last:mb-0">
      {/* Unit Trend Progress Bar */}
      {hasTrending && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className="text-sm font-medium text-blue-700 whitespace-nowrap">
                {trendLabel}
              </span>
              <div className="flex-1 bg-blue-200 rounded-full h-2.5 min-w-[60px]">
                <div
                  className="h-2.5 rounded-full transition-all duration-500 bg-blue-600"
                  style={{ width: `${trendPercent}%` }}
                />
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 text-sm flex-wrap justify-center sm:justify-end">
              <span className="text-gray-700 whitespace-nowrap">
                <span className="font-semibold text-blue-600">
                  {combinedTotal}
                </span>
                <span className="text-gray-500 text-xs sm:text-sm"> Units Sold</span>
              </span>
              <span className="text-gray-300 hidden sm:inline">|</span>
              <span className="text-gray-300 sm:hidden">·</span>
              <span className="text-gray-700 whitespace-nowrap">
                <span className="font-semibold text-blue-600">{trendingUnits}</span>
                <span className="text-gray-500 text-xs sm:text-sm"> Trending</span>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface UnitSectionProps {
  title: string;
  currentUnits: Record<string, number>;
  lastYearUnits: Record<string, number> | null;
  averageUnits: Record<string, number> | null;
  totalCurrent: number;
  totalLastYear: number | null;
  totalAverage: number | null;
  currentYear: number;
  /** Unprocessed units by brand/category */
  unprocessedUnits?: Record<string, number>;
  /** Total unprocessed units */
  totalUnprocessed?: number;
  /** If true, unprocessed keys may not match current keys (used for Used vehicles) */
  unprocessedKeysMismatch?: boolean;
  /** Trending units - projected end-of-month based on current performance */
  trendingUnits?: number | null;
  /** Label for the trend bar (e.g., "New Unit Trend" or "Used Unit Trend") */
  trendLabel?: string;
  /** Target units from projections */
  targetUnits?: number | null;
  /** Swap units (dealer trades — shown separately, not counted toward sold units) */
  swapUnits?: number;
  /** Wholesale units (shown separately, not counted toward sold units) */
  wholesaleUnits?: number;
  /** Callback when "Units Sold" is clicked */
  onUnitsClick?: () => void;
}

const UnitSection: React.FC<UnitSectionProps> = ({
  title,
  currentUnits,
  lastYearUnits,
  averageUnits,
  totalCurrent,
  totalLastYear,
  currentYear,
  unprocessedUnits,
  totalUnprocessed = 0,
  unprocessedKeysMismatch = false,
  trendingUnits,
  trendLabel = 'Unit Trend',
  targetUnits,
  swapUnits = 0,
  wholesaleUnits = 0,
  onUnitsClick,
}) => {
  const hasUnprocessed = totalUnprocessed > 0;
  const combinedTotal = totalCurrent + totalUnprocessed;
  const hasTrending = trendingUnits !== null && trendingUnits !== undefined && trendingUnits > 0;

  // Check if any unprocessed keys match current keys
  // If not, we need to handle differently (add all unprocessed to first bar)
  const currentKeys = Object.keys(currentUnits);
  const unprocessedKeys = Object.keys(unprocessedUnits || {});
  const keysMatch = unprocessedKeys.length === 0 ||
    unprocessedKeys.some(key => currentKeys.includes(key));

  // For used vehicles (unprocessedKeysMismatch=true), add all unprocessed to first bar with units
  // This handles the case where unprocessed tracks by make but processed tracks by account category
  let effectiveUnprocessedUnits = unprocessedUnits;
  if (hasUnprocessed && (unprocessedKeysMismatch || !keysMatch)) {
    // Find the first category with processed units (or first category)
    const sortedByCount = Object.entries(currentUnits)
      .sort(([, a], [, b]) => b - a);
    const primaryCategory = sortedByCount.length > 0 ? sortedByCount[0][0] : null;

    if (primaryCategory) {
      // Assign all unprocessed to primary category
      effectiveUnprocessedUnits = { [primaryCategory]: totalUnprocessed };
    }
  }

  // Sort brands: non-zero first (by count descending), then zeros (alphabetical)
  // Include unprocessed in sort calculation
  const sortedBrands = Object.entries(currentUnits)
    .sort(([brandA, countA], [brandB, countB]) => {
      const totalA = countA + (effectiveUnprocessedUnits?.[brandA] ?? 0);
      const totalB = countB + (effectiveUnprocessedUnits?.[brandB] ?? 0);
      // Non-zero counts first, sorted descending
      if (totalA > 0 && totalB > 0) return totalB - totalA;
      if (totalA > 0) return -1;
      if (totalB > 0) return 1;
      // Both are zero - sort alphabetically
      return brandA.localeCompare(brandB);
    });

  if (sortedBrands.length === 0 && combinedTotal === 0) {
    return null;
  }

  // Calculate max value for consistent bar scaling across all brands
  // Include unprocessed units in the calculation
  const allValues = sortedBrands.flatMap(([brand]) => [
    (currentUnits[brand] ?? 0) + (effectiveUnprocessedUnits?.[brand] ?? 0),
    lastYearUnits?.[brand] ?? 0,
    averageUnits?.[brand] ?? 0,
  ]);
  const maxValue = Math.max(...allValues, 1);

  // Variance from target (for header display)
  const targetVariance = targetUnits !== null && targetUnits !== undefined ? combinedTotal - targetUnits : null;

  // Calculate trend progress (current sold vs trending end-of-month)
  // Bar fills based on sold/trending ratio
  const trendPercent = hasTrending && trendingUnits! > 0
    ? Math.min(Math.round((combinedTotal / trendingUnits!) * 100), 100)
    : 0;

  return (
    <div className="mb-4 last:mb-0">
      {/* Unit Trend Progress Bar - slim horizontal bar */}
      {hasTrending && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            {/* Label + Progress Bar */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className="text-sm font-medium text-blue-700 whitespace-nowrap">
                {trendLabel}
              </span>
              {/* Progress Bar */}
              <div className="flex-1 bg-blue-200 rounded-full h-2.5 min-w-[60px]">
                <div
                  className="h-2.5 rounded-full transition-all duration-500 bg-blue-600"
                  style={{ width: `${trendPercent}%` }}
                />
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-2 sm:gap-3 text-sm flex-wrap justify-center sm:justify-end">
              <span className="text-gray-700 whitespace-nowrap">
                <span className="font-semibold text-blue-600">
                  {combinedTotal}
                </span>
                <span className="text-gray-500 text-xs sm:text-sm"> Units Sold</span>
              </span>
              <span className="text-gray-300 hidden sm:inline">|</span>
              <span className="text-gray-300 sm:hidden">·</span>
              <span className="text-gray-700 whitespace-nowrap">
                <span className="font-semibold text-blue-600">{trendingUnits}</span>
                <span className="text-gray-500 text-xs sm:text-sm"> Trending</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
        <div className="flex items-center gap-2">
          {/* Units Sold — clickable when onUnitsClick is provided */}
          <div
            className={`flex items-center gap-1 ${onUnitsClick ? 'cursor-pointer hover:underline hover:text-blue-700 transition-colors' : ''}`}
            onClick={onUnitsClick}
            title={onUnitsClick ? 'Click to view deal details' : undefined}
          >
            <span className={`text-lg font-bold ${onUnitsClick ? 'text-blue-600' : 'text-gray-900'}`}>{combinedTotal}</span>
            <span className="text-xs text-gray-500">Sold</span>
            {hasUnprocessed && (
              <span className="text-xs text-gray-400">({totalCurrent}+{totalUnprocessed})</span>
            )}
          </div>
          {/* Swap units (dealer trades) — shown separately */}
          {swapUnits > 0 && (
            <>
              <span className="text-sm text-gray-300">|</span>
              <div className="flex items-center gap-1">
                <span className="text-lg font-bold text-blue-500">{swapUnits}</span>
                <span className="text-xs text-gray-500">Swaps</span>
              </div>
            </>
          )}
          {/* Wholesale units — shown separately */}
          {wholesaleUnits > 0 && (
            <>
              <span className="text-sm text-gray-300">|</span>
              <div className="flex items-center gap-1">
                <span className="text-lg font-bold text-purple-500">{wholesaleUnits}</span>
                <span className="text-xs text-gray-500">Wholesale</span>
              </div>
            </>
          )}
          {/* Target from projections */}
          {targetUnits !== null && targetUnits !== undefined && (
            <>
              <span className="text-sm text-gray-400">/</span>
              <div className="flex items-center gap-1">
                <span className="text-lg font-bold text-gray-900">{targetUnits}</span>
                <span className="text-xs text-gray-500">Target</span>
              </div>
            </>
          )}
          {/* Variance from target */}
          {targetVariance !== null && (
            <span className={`text-sm font-medium ${targetVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ({targetVariance >= 0 ? '+' : ''}{targetVariance})
            </span>
          )}
        </div>
      </div>

      {/* Brand tiles - responsive grid */}
      {sortedBrands.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {sortedBrands.map(([brand, count]) => (
            <BrandTile
              key={brand}
              brand={brand}
              currentUnits={count}
              lastYearUnits={lastYearUnits?.[brand] ?? null}
              averageUnits={averageUnits?.[brand] ?? null}
              currentYear={currentYear}
              maxValue={maxValue}
              unprocessedUnits={effectiveUnprocessedUnits?.[brand] ?? 0}
            />
          ))}
        </div>
      ) : (
        <div className="text-sm text-gray-500 italic">No units by brand</div>
      )}
    </div>
  );
};

/**
 * Calculate average units by brand from multiple years of comparison data
 */
function calculateAverageUnits(
  comparisonByYear: Record<number, YearComparisonData> | undefined,
  currentYear: number,
  isNewVehicle: boolean,
  configuredBrands: string[]
): { brandAverages: Record<string, number>; totalAverage: number; yearsIncluded: number[] } | null {
  if (!comparisonByYear) return null;

  // Get last 3 years (excluding current year)
  const yearsToAverage = [currentYear - 1, currentYear - 2, currentYear - 3];
  const availableYears: number[] = [];
  const brandTotals: Record<string, number[]> = {};
  let totalUnitsList: number[] = [];

  // Initialize brand totals for configured brands
  for (const brand of configuredBrands) {
    brandTotals[brand] = [];
  }

  for (const year of yearsToAverage) {
    const yearData = comparisonByYear[year];
    if (yearData?.hasData && yearData.unitsAtPoint) {
      availableYears.push(year);

      const units = isNewVehicle
        ? yearData.unitsAtPoint.newVehicleUnits
        : yearData.unitsAtPoint.usedVehicleUnits;

      const totalUnits = isNewVehicle
        ? yearData.unitsAtPoint.totalNewUnits
        : yearData.unitsAtPoint.totalUsedUnits;

      totalUnitsList.push(totalUnits);

      // Collect brand data
      for (const brand of configuredBrands) {
        brandTotals[brand].push(units[brand] ?? 0);
      }
    }
  }

  if (availableYears.length === 0) return null;

  // Calculate averages
  const brandAverages: Record<string, number> = {};
  for (const [brand, values] of Object.entries(brandTotals)) {
    if (values.length > 0) {
      brandAverages[brand] = values.reduce((a, b) => a + b, 0) / values.length;
    } else {
      brandAverages[brand] = 0;
    }
  }

  const totalAverage = totalUnitsList.length > 0
    ? totalUnitsList.reduce((a, b) => a + b, 0) / totalUnitsList.length
    : 0;

  return { brandAverages, totalAverage, yearsIncluded: availableYears };
}

/**
 * Calculate trending units (projected end-of-month) using Time-Weighted Blend.
 * Uses weighted 50/30/20 percentages from the last 3 years for historical average.
 *
 * Time-Weighted Blend formula:
 * - currentPace = (currentUnits / daysElapsed) * totalWorkingDays
 * - weight = daysElapsed / totalWorkingDays
 * - trending = (currentPace * weight) + (historicalAvg * (1 - weight))
 *
 * @param comparisonByYear - Historical comparison data by year
 * @param currentYear - The current year
 * @param currentUnits - Current units sold MTD
 * @param isNewVehicle - Whether calculating for new or used vehicles
 * @param daysElapsed - Number of working days completed
 * @param totalWorkingDays - Total working days in the month
 * @returns Trending units (projected end-of-month) or null if insufficient data
 */
function calculateUnitTrendingWithDays(
  comparisonByYear: Record<number, YearComparisonData> | undefined,
  currentYear: number,
  currentUnits: number,
  isNewVehicle: boolean,
  daysElapsed?: number,
  totalWorkingDays?: number
): number | null {
  if (!comparisonByYear) return null;

  // Standard weights: 50% for year-1, 30% for year-2, 20% for year-3
  const baseWeights = [0.50, 0.30, 0.20];
  const yearsToCheck = [currentYear - 1, currentYear - 2, currentYear - 3];

  // Collect historical data from years with complete data
  const historicalData: { weight: number; atPoint: number; atEnd: number }[] = [];

  for (let i = 0; i < yearsToCheck.length; i++) {
    const year = yearsToCheck[i];
    const yearData = comparisonByYear[year];

    if (yearData?.hasData && yearData.unitsAtPoint && yearData.finalMonthUnits) {
      const unitsAtPoint = isNewVehicle
        ? yearData.unitsAtPoint.totalNewUnits
        : yearData.unitsAtPoint.totalUsedUnits;
      const finalUnits = isNewVehicle
        ? yearData.finalMonthUnits.totalNewUnits
        : yearData.finalMonthUnits.totalUsedUnits;

      // Only include if we have valid data
      if (finalUnits > 0) {
        historicalData.push({ weight: baseWeights[i], atPoint: unitsAtPoint, atEnd: finalUnits });
      }
    }
  }

  if (historicalData.length === 0) return null;

  // Calculate weighted average at month end
  const totalWeight = historicalData.reduce((sum, d) => sum + d.weight, 0);
  const historicalAvgAtMonthEnd = historicalData.reduce(
    (sum, d) => sum + (d.atEnd * d.weight / totalWeight),
    0
  );

  // Use Time-Weighted Blend if we have working days info
  if (daysElapsed && daysElapsed > 0 && totalWorkingDays && totalWorkingDays > 0) {
    return calculateTrendingUnits(currentUnits, daysElapsed, totalWorkingDays, historicalAvgAtMonthEnd);
  }

  // Fallback: return historical average if no working days info
  return Math.round(historicalAvgAtMonthEnd);
}

export const UnitCountsCard: React.FC<UnitCountsCardProps> = ({
  currentYear,
  currentUnits,
  lastYearData,
  comparisonByYear,
  daysRemaining,
  daysElapsed,
  totalWorkingDays,
  selectedDepartment = 'ALL',
  locationId,
  unprocessedUnits,
  swapNewUnits = 0,
  wholesaleUsedUnits = 0,
  targetUnits,
  onUnitsClick,
}) => {
  if (!currentUnits) {
    return null;
  }

  // Consolidated view (locationId=0) shows simplified totals only (no per-brand breakdown)
  const isConsolidated = locationId === 0;

  // Extract unprocessed data (if available)
  const hasUnprocessed = unprocessedUnits && (unprocessedUnits.totalNewUnits > 0 || unprocessedUnits.totalUsedUnits > 0);

  // Calculate trending units for new and used vehicles
  const newUnitsTotal = currentUnits.totalNewUnits + (unprocessedUnits?.totalNewUnits ?? 0);
  const usedUnitsTotal = currentUnits.totalUsedUnits + (unprocessedUnits?.totalUsedUnits ?? 0);

  const newVehicleTrending = calculateUnitTrendingWithDays(comparisonByYear, currentYear, newUnitsTotal, true, daysElapsed, totalWorkingDays);
  const usedVehicleTrending = calculateUnitTrendingWithDays(comparisonByYear, currentYear, usedUnitsTotal, false, daysElapsed, totalWorkingDays);

  const lastYearUnits = lastYearData?.unitsAtPoint || null;

  // Get configured brands for this location
  const locationBrands = getLocationBrands(locationId);

  // Ensure all configured brands are present (even with 0 units)
  const newVehicleUnitsWithAllBrands = ensureAllBrands(
    currentUnits.newVehicleUnits,
    locationBrands.newVehicleBrands
  );
  const usedVehicleUnitsWithAllBrands = ensureAllBrands(
    currentUnits.usedVehicleUnits,
    locationBrands.usedVehicleBrands
  );

  // Also ensure all brands for last year data
  const lastYearNewUnits = lastYearUnits
    ? ensureAllBrands(lastYearUnits.newVehicleUnits, locationBrands.newVehicleBrands)
    : null;
  const lastYearUsedUnits = lastYearUnits
    ? ensureAllBrands(lastYearUnits.usedVehicleUnits, locationBrands.usedVehicleBrands)
    : null;

  // Calculate 3-year averages
  const newVehicleAverages = calculateAverageUnits(
    comparisonByYear,
    currentYear,
    true,
    locationBrands.newVehicleBrands
  );
  const usedVehicleAverages = calculateAverageUnits(
    comparisonByYear,
    currentYear,
    false,
    locationBrands.usedVehicleBrands
  );

  // Determine what to show based on selected department
  const showNewVehicles = selectedDepartment === 'ALL' || selectedDepartment === 'NEW_VEHICLE';
  const showUsedVehicles = selectedDepartment === 'ALL' || selectedDepartment === 'USED_VEHICLE';
  const showTotal = selectedDepartment === 'ALL';

  // Generate title based on department
  const cardTitle = selectedDepartment === 'NEW_VEHICLE'
    ? 'New Vehicle Units Sold'
    : selectedDepartment === 'USED_VEHICLE'
      ? 'Used Vehicle Units Sold'
      : 'Units Sold';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{cardTitle}</h3>
          <p className="text-xs text-gray-500">{daysRemaining} working days remaining</p>
        </div>
        {/* Legend - only show for non-consolidated view */}
        {!isConsolidated && (
          <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.currentYear }} />
              <span>{currentYear}</span>
            </div>
            {hasUnprocessed && (
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.unprocessed }} />
                <span>Unprocessed</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.lastYear }} />
              <span>{currentYear - 1}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.average }} />
              <span>3Y Avg</span>
            </div>
          </div>
        )}
      </div>

      {/* Consolidated View - simplified totals only (no per-brand breakdown) */}
      {isConsolidated ? (
        <>
          {/* New Vehicles Section - Consolidated */}
          {showNewVehicles && (
            <ConsolidatedUnitSection
              title="New Vehicles"
              totalCurrent={currentUnits.totalNewUnits}
              totalLastYear={lastYearUnits?.totalNewUnits ?? null}
              currentYear={currentYear}
              totalUnprocessed={unprocessedUnits?.totalNewUnits ?? 0}
              trendingUnits={newVehicleTrending}
              trendLabel="New Unit Trend"
            />
          )}

          {/* Used Vehicles Section - Consolidated */}
          {showUsedVehicles && (
            <ConsolidatedUnitSection
              title="Used Vehicles"
              totalCurrent={currentUnits.totalUsedUnits}
              totalLastYear={lastYearUnits?.totalUsedUnits ?? null}
              currentYear={currentYear}
              totalUnprocessed={unprocessedUnits?.totalUsedUnits ?? 0}
              trendingUnits={usedVehicleTrending}
              trendLabel="Used Unit Trend"
            />
          )}
        </>
      ) : (
        <>
          {/* New Vehicles Section */}
          {showNewVehicles && (
            <UnitSection
              title="New Vehicles"
              currentUnits={newVehicleUnitsWithAllBrands}
              lastYearUnits={lastYearNewUnits}
              averageUnits={newVehicleAverages?.brandAverages ?? null}
              totalCurrent={currentUnits.totalNewUnits}
              totalLastYear={lastYearUnits?.totalNewUnits ?? null}
              totalAverage={newVehicleAverages?.totalAverage ?? null}
              currentYear={currentYear}
              unprocessedUnits={unprocessedUnits?.unitsByBrand}
              totalUnprocessed={unprocessedUnits?.totalNewUnits ?? 0}
              trendingUnits={newVehicleTrending}
              trendLabel="New Unit Trend"
              targetUnits={selectedDepartment === 'NEW_VEHICLE' ? targetUnits : null}
              swapUnits={swapNewUnits}
              onUnitsClick={onUnitsClick ? () => onUnitsClick('NEW_VEHICLE') : undefined}
            />
          )}

          {/* Used Vehicles Section */}
          {/* Note: unprocessedKeysMismatch=true because unprocessed tracks by make (Ford, Chrysler)
              but processed tracks by account category (FVA, VC, Used) */}
          {showUsedVehicles && (
            <UnitSection
              title="Used Vehicles"
              currentUnits={usedVehicleUnitsWithAllBrands}
              lastYearUnits={lastYearUsedUnits}
              averageUnits={usedVehicleAverages?.brandAverages ?? null}
              totalCurrent={currentUnits.totalUsedUnits}
              totalLastYear={lastYearUnits?.totalUsedUnits ?? null}
              totalAverage={usedVehicleAverages?.totalAverage ?? null}
              currentYear={currentYear}
              unprocessedUnits={unprocessedUnits?.unitsByCategory}
              totalUnprocessed={unprocessedUnits?.totalUsedUnits ?? 0}
              unprocessedKeysMismatch={true}
              trendingUnits={usedVehicleTrending}
              trendLabel="Used Unit Trend"
              targetUnits={selectedDepartment === 'USED_VEHICLE' ? targetUnits : null}
              wholesaleUnits={wholesaleUsedUnits}
              onUnitsClick={onUnitsClick ? () => onUnitsClick('USED_VEHICLE') : undefined}
            />
          )}
        </>
      )}

      {/* Combined Total - only show when viewing ALL departments */}
      {showTotal && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Total Units</span>
            <div className="flex items-center gap-2">
              {(() => {
                const processedTotal = currentUnits.totalNewUnits + currentUnits.totalUsedUnits;
                const unprocessedTotal = (unprocessedUnits?.totalNewUnits ?? 0) + (unprocessedUnits?.totalUsedUnits ?? 0);
                const combinedTotal = processedTotal + unprocessedTotal;
                const totalTrending = (newVehicleTrending ?? 0) + (usedVehicleTrending ?? 0);
                const targetVariance = targetUnits !== null && targetUnits !== undefined ? combinedTotal - targetUnits : null;

                // Consolidated view: show "X Units Sold / X Units Trending"
                if (isConsolidated) {
                  return (
                    <span className="text-sm text-gray-700">
                      <span className="font-bold text-gray-900">{combinedTotal}</span>
                      <span className="text-gray-500"> Units Sold</span>
                      <span className="text-gray-400 mx-1">/</span>
                      <span className="font-bold text-gray-900">{totalTrending}</span>
                      <span className="text-gray-500"> Units Trending</span>
                    </span>
                  );
                }

                // Non-consolidated view: show "X Sold / Y Target (variance)"
                return (
                  <>
                    {/* Units Sold */}
                    <div className="flex items-center gap-1">
                      <span className="text-xl font-bold text-gray-900">{combinedTotal}</span>
                      <span className="text-xs text-gray-500">Sold</span>
                      {unprocessedTotal > 0 && (
                        <span className="text-xs text-gray-400">({processedTotal}+{unprocessedTotal})</span>
                      )}
                    </div>
                    {/* Target from projections */}
                    {targetUnits !== null && targetUnits !== undefined && (
                      <>
                        <span className="text-sm text-gray-400">/</span>
                        <div className="flex items-center gap-1">
                          <span className="text-xl font-bold text-gray-900">{targetUnits}</span>
                          <span className="text-xs text-gray-500">Target</span>
                        </div>
                      </>
                    )}
                    {/* Variance from target */}
                    {targetVariance !== null && (
                      <span className={`text-sm font-medium ${targetVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ({targetVariance >= 0 ? '+' : ''}{targetVariance})
                      </span>
                    )}
                    {/* Swap units */}
                    {swapNewUnits > 0 && (
                      <>
                        <span className="text-sm text-gray-300">|</span>
                        <div className="flex items-center gap-1">
                          <span className="text-xl font-bold text-blue-500">{swapNewUnits}</span>
                          <span className="text-xs text-gray-500">Swaps</span>
                        </div>
                      </>
                    )}
                    {/* Wholesale units */}
                    {wholesaleUsedUnits > 0 && (
                      <>
                        <span className="text-sm text-gray-300">|</span>
                        <div className="flex items-center gap-1">
                          <span className="text-xl font-bold text-purple-500">{wholesaleUsedUnits}</span>
                          <span className="text-xs text-gray-500">Wholesale</span>
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
